import React, { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaEye, FaHeadset, FaStar } from "react-icons/fa";

const COUNTING_STYLES = `
  @keyframes cnt-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }
  @keyframes cnt-pulse-ring {
    0% { box-shadow: 0 12px 30px -10px rgba(217,119,6,0.45), 0 0 0 0 rgba(217,119,6,0.35); }
    70% { box-shadow: 0 12px 30px -10px rgba(217,119,6,0.45), 0 0 0 14px rgba(217,119,6,0); }
    100% { box-shadow: 0 12px 30px -10px rgba(217,119,6,0.45), 0 0 0 0 rgba(217,119,6,0); }
  }
  .cnt-icon-float { animation: cnt-float 3.5s ease-in-out infinite; }
  .cnt-icon-pulse { animation: cnt-pulse-ring 2.5s ease-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .cnt-icon-float, .cnt-icon-pulse { animation: none !important; }
  }
`;

function Counting() {
  const [counts, setCounts] = useState({
    impressions: 0,
    support: 24,
    satisfaction: 0,
  });

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (inView) {
      let impressions = 0;
      let satisfaction = 0;

      const updateCount = () => {
        setCounts((prev) => ({
          impressions: impressions < 900 ? (impressions += 15) : 900,
          support: 24,
          satisfaction: satisfaction < 100 ? (satisfaction += 2) : 100,
        }));

        if (impressions < 900 || satisfaction < 100) {
          setTimeout(updateCount, 18);
        }
      };

      updateCount();
    }
  }, [inView]);

  const stats = [
    { icon: <FaEye />, value: counts.impressions, suffix: "M+", label: "Ad impressions served" },
    { icon: <FaHeadset />, value: counts.support, suffix: "/7", label: "Customer support" },
    { icon: <FaStar />, value: counts.satisfaction, suffix: "%", label: "Satisfied customers" },
  ];

  return (
    <section
      className="relative py-12 sm:py-16 md:py-20 font-sans overflow-hidden border-t border-[#F5DFC5]"
      style={{
        background:
          "radial-gradient(90% 70% at 85% 0%, #FFF4E5 0%, transparent 55%), radial-gradient(80% 60% at 5% 100%, #FEF3E5 0%, transparent 55%), #FFFBF5",
      }}
      ref={ref}
    >
      <style>{COUNTING_STYLES}</style>

      {/* Ambient glow, same treatment as About/Contact */}
      <div
        aria-hidden="true"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] opacity-[0.1] blur-[100px] pointer-events-none"
        style={{ background: "radial-gradient(circle, #D97706, transparent 70%)" }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 lg:gap-10">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              className="flex flex-col items-center text-center px-2"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={inView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: i * 0.15 + 0.1, type: "spring", stiffness: 200 }}
                className={`mb-4 sm:mb-5 ${!prefersReducedMotion ? "cnt-icon-float" : ""}`}
                style={{ animationDelay: `${i * 0.3}s` }}
              >
                <div
                  className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center ring-8 hover:scale-105 transition-transform duration-300 ${
                    !prefersReducedMotion ? "cnt-icon-pulse" : ""
                  }`}
                  style={{
                    background: "linear-gradient(145deg, #D97706, #EA580C)",
                    "--tw-ring-color": "#FDE8CD",
                    animationDelay: `${i * 0.4}s`,
                  }}
                >
                  <div className="text-white text-xl sm:text-2xl">
                    {stat.icon}
                  </div>
                </div>
              </motion.div>

              <h3
                className="text-[#1C1410] text-xl sm:text-2xl md:text-3xl font-extrabold tracking-tight mb-2 font-mono tabular-nums"
                style={{ fontFamily: "'Fraunces', ui-serif, Georgia, serif" }}
              >
                {stat.value}
                {stat.suffix}
              </h3>
              <p className="text-[#4D3E30] text-xs sm:text-sm max-w-[180px] sm:max-w-none">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Counting;